import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAnnouncementBanner } from '../services/announcementService';

interface AnnouncementBannerProps {
  message?: string;
  releaseDate?: string;
  onClose?: () => void;
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  message: propMessage,
  releaseDate: propReleaseDate,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [message, setMessage] = useState(propMessage || '');
  const [releaseDate, setReleaseDate] = useState(propReleaseDate || '');
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaText, setCtaText] = useState('');

  // Fetch announcement from Firestore if not provided via props
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        if (!propMessage || !propReleaseDate) {
          const announcement = await getAnnouncementBanner();
          if (announcement && announcement.isActive) {
            setMessage(announcement.message);
            setReleaseDate(announcement.releaseDate);
            setCtaUrl(announcement.ctaUrl || '');
            setCtaText(announcement.ctaText || '');
          } else if (!propMessage || !propReleaseDate) {
            // Hide banner if no announcement is active and no props provided
            setIsVisible(false);
          }
        }
      } catch (error) {
        console.error('Error fetching announcement:', error);
      }
    };

    fetchAnnouncement();
  }, [propMessage, propReleaseDate]);

  // ⏱️ Check if closed within last 24h
  useEffect(() => {
    const closedAt = localStorage.getItem('announcementBannerClosedAt');
    if (closedAt) {
      const closedDate = new Date(closedAt);
      const now = new Date();
      const hoursDiff = (now.getTime() - closedDate.getTime()) / 1000 / 60 / 60;
      if (hoursDiff < 24) {
        setIsVisible(false);
      }
    }

    // Nettoyage ancien système (définitif)
    localStorage.removeItem('announcementBannerClosed');
  }, []);

  // Measure content and container width for animation
  useEffect(() => {
    if (contentRef.current && containerRef.current) {
      setContentWidth(contentRef.current.scrollWidth);
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (contentRef.current && containerRef.current) {
        setContentWidth(contentRef.current.scrollWidth);
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, message]);

  // Dispatch custom event when banner visibility changes
  useEffect(() => {
    // Dispatch event to notify other components
    const event = new CustomEvent('announcementBannerVisibilityChange', { 
      detail: { isVisible } 
    });
    document.dispatchEvent(event);
    
    // Update body class for global styling
    if (isVisible) {
      document.body.classList.add('has-announcement-banner');
    } else {
      document.body.classList.remove('has-announcement-banner');
    }
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('announcementBannerClosedAt', new Date().toISOString());
    if (onClose) onClose();
  };

  // Format the message with the release date
  const formattedMessage = message.replace('[DATE]', releaseDate);

  // Calculate animation duration based on content length
  const animationDuration = Math.max(10, contentWidth / 50);

  if (!isVisible || !message || !releaseDate) return null;

  return (
    <AnimatePresence>
      {isVisible && message && releaseDate && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="fixed top-0 left-0 right-0 z-[99] backdrop-blur-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md border-b border-white/50"
        >
          <div
            className="relative flex items-center h-12 overflow-hidden px-4"
            ref={containerRef}
            aria-live="polite"
          >
            <div className="absolute inset-y-0 left-0 w-8 z-10 bg-gradient-to-r from-blue-600 to-transparent pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-20 z-10 bg-gradient-to-l from-indigo-600 to-transparent flex items-center justify-end pr-3">
              <button
                onClick={handleClose}
                className="p-1.5 rounded-full transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-label="Fermer l'annonce"
                title="Ne plus afficher pendant 24h"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
  
            <div className="flex overflow-hidden w-full items-center">
              <motion.div
                ref={contentRef}
                className="whitespace-nowrap text-sm tracking-wide font-normal flex items-center gap-8"
                animate={{
                  x: [-20, -(contentWidth + 20)],
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: animationDuration,
                    ease: "linear",
                  },
                }}
              >
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span>{formattedMessage}</span>
                    {ctaUrl && (
                      <a
                        href={ctaUrl}
                        className="px-3 py-1 text-xs rounded-full border border-white/20 text-white hover:border-white/40 transition"
                      >
                        {ctaText || 'Découvrir'}
                      </a>
                    )}
                    {index < 2 && <span className="text-white/40 ml-4">•</span>}
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementBanner;